// src/utils/DeleteConfirmationModal.jsx
import React, { useState, useEffect } from 'react';
import { createPortal } from "react-dom";
import ThemedSelect from './ThemedSelect';
import { getInitials, getRandomColor } from './UserCustomDisplay';

const OWNERSHIP_COUNT_ROWS = [
  ['projects', 'Projects'],
  ['data_collections', 'Data Folders'],
  ['country_headships', 'Country headships']
];

const DeleteConfirmationModal = ({
  show,
  onClose,
  onConfirm,
  objectName = "item",
  itemName,
  bodyContent,
  requireOwnerTransfer = false,
  ownerOptions = [],
  ownerOptionsLoading = false,
  ownershipCounts = null,
  ownsAnything = true,
  onTransferOwnership = null
}) => {
  const [selectedOwnerId, setSelectedOwnerId] = useState(null);
  const [transferDone, setTransferDone] = useState(false);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    if (show) {
      setSelectedOwnerId(null);
      setTransferDone(false);
      setTransferring(false);
    }
  }, [show]);

  if (!show) return null;

  const gateApplies = requireOwnerTransfer && ownsAnything;
  const canDelete = !gateApplies || transferDone;

  const handleSelectOwner = (id) => {
    setSelectedOwnerId(id);
  };

  const handleTransfer = async () => {
    if (!selectedOwnerId || !onTransferOwnership) return;
    setTransferring(true);
    try {
      const ok = await onTransferOwnership(selectedOwnerId);
      if (ok) setTransferDone(true);
    } finally {
      setTransferring(false);
    }
  };

  return createPortal(
    <div className="modal fade show d-block bg-dark bg-opacity-50" style={{ zIndex: "var(--num-1000000000 )" }}>
      <div className={`modal-dialog modal-dialog-centered${gateApplies ? ' modal-dialog-scrollable' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <p className="mb-0 ev-fs-15 ev-bold-med">
              Confirm Delete {objectName.charAt(0).toUpperCase() + objectName.slice(1)}
            </p>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body ev-fs-12">
            <p>
              Are you sure you want to delete the {objectName}: <strong>{itemName}</strong>?
            </p>
            {bodyContent && (
              <div>
                {bodyContent}
              </div>
            )}

            {transferDone && (
              <p className="ev-small-p-dark mt-2 mb-0">
                <i className="bi bi-check-circle me-1"></i>
                Ownership transferred. You can delete the {objectName} now.
              </p>
            )}

            {!transferDone && requireOwnerTransfer && !ownsAnything && !ownerOptionsLoading && (
              <p className="ev-small-p-dark mt-2 mb-0">
                <i className="bi bi-check-circle me-1"></i>
                This {objectName} holds no projects or Data Folders, so there is
                nothing to transfer.
              </p>
            )}

            {!transferDone && gateApplies && (
              <div className="mt-3">
                <p className="text-danger mb-2">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Everything this {objectName} owns has to be handed to another user before
                  it can be deleted.
                </p>

                {ownershipCounts && (
                  <div className="ev-card-2 mb-3">
                    <div className="row">
                      {OWNERSHIP_COUNT_ROWS
                        .filter(([key]) => ownershipCounts[key] > 0)
                        .map(([key, label]) => (
                          <div key={key} className="col-6 mb-2 d-flex align-items-center gap-2">
                            <span className="ev-message-card1 ev-fs-11">
                              {ownershipCounts[key]}
                            </span>
                            <span className="ev-small-p-dark">{label}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="ev-reg-p-dark mb-1">
                    Transfer ownership to <span className="text-danger">*</span>
                  </label>
                  <ThemedSelect
                    value={selectedOwnerId || ''}
                    ariaLabel="Transfer ownership to"
                    placeholder={ownerOptionsLoading
                      ? 'Loading users'
                      : ownerOptions.length === 0
                        ? 'No eligible user available'
                        : 'Select user'}
                    disabled={ownerOptionsLoading || ownerOptions.length === 0}
                    enableSearch={true}
                    searchPlaceholder="Search user"
                    menuClassName="delete-owner-select__menu"
                    flipMenuUpwards={requireOwnerTransfer}
                    options={ownerOptions.map(opt => ({
                      value: opt.id,
                      label: opt.name || opt.label,
                      hint: opt.email,
                      badge: opt.role,
                      avatarText: getInitials(opt.email || opt.name || opt.label || ''),
                      avatarColor: getRandomColor(opt.email || opt.name || opt.label || '')
                    }))}
                    onChange={(nextId) => handleSelectOwner(nextId || null)}
                  />
                  {!ownerOptionsLoading && ownerOptions.length === 0 && (
                    <p className="text-danger mt-1 mb-0">
                      There is no other user who can take over this {objectName}'s ownership,
                      so it cannot be deleted yet.
                    </p>
                  )}
                </div>

                <div className="mt-3">
                  <button
                    type="button"
                    className="btn ev-btn-blue-fill-round ev-btn-med"
                    disabled={!selectedOwnerId || transferring}
                    onClick={handleTransfer}
                  >
                    {transferring ? 'Transferring...' : 'Transfer Ownership'}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary ev-btn-reg"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger ev-btn-reg"
              onClick={onConfirm}
              disabled={!canDelete}
            >
              Delete {objectName.charAt(0).toUpperCase() + objectName.slice(1)}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DeleteConfirmationModal;
